/**
 * ReportsV2.js — Tank reports (Consumption / Filled) for OHTs and Sumps.
 * Route: /reports
 *
 * Backend contract (to be implemented by the API):
 *   GET /user/reports/tank-summary?device=oht|sump&metric=consumed|filled&from=YYYY-MM&to=YYYY-MM
 *   (org resolved from the auth token)
 *   → {
 *       device: "oht",
 *       metric: "consumed",
 *       months: ["2026-06","2026-07","2026-08"],           // ascending, YYYY-MM
 *       tanks: [
 *         { sensor_id, name, values: [<litres>, ...] }      // values.length === months.length
 *       ]
 *     }
 *   The monthly litres MUST be computed with the same deadband-follower + dwell
 *   used by the Volume tab, so report totals match the per-sensor screen.
 */

import axios from "axios";
import React, { useCallback, useContext, useRef, useState } from "react";
import { useHistory } from "react-router-dom";
import moment from "moment";
import styled from "styled-components";
import {
  Chart as ChartJS, CategoryScale, LinearScale, BarElement, Tooltip, Legend,
} from "chart.js";
import { Bar } from "react-chartjs-2";
import { GlobalState } from "../../GlobalState";
import { literToKiloliter } from "../utils/utils";
import PullToRefresh from "../utils/PullToRefresh";
import { Logo } from "../../assets/svg/index";

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip, Legend);

const GREEN = "#237A35";
const MID = "#52B96A";
const AMBER = "#EA580C";
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const FIRST_YEAR = 2024;

/* ── styled ── */
const Screen = styled.div`
  background: #F4F6F9; min-height: 100vh;
  font-family: 'Inter', -apple-system, sans-serif;
  padding-bottom: 40px;
`;
const Nav = styled.div`
  background: #fff; padding: 14px 18px;
  display: flex; align-items: center; gap: 12px;
  border-bottom: 1px solid #EAECEF; position: sticky; top: 0; z-index: 20;
`;
const BackBtn = styled.button`
  width: 36px; height: 36px; border-radius: 50%;
  border: 1.5px solid #E5E7EB; background: #fff;
  display: flex; align-items: center; justify-content: center;
  font-size: 18px; cursor: pointer; color: #374151;
  &:hover { border-color: ${GREEN}; color: ${GREEN}; }
`;
const NavTitle = styled.div` font-size: 17px; font-weight: 700; color: #111; `;
const Page = styled.div` max-width: 760px; margin: 0 auto; padding: 18px; `;
const Card = styled.div`
  background: #fff; border: 1px solid #EAECEF; border-radius: 16px;
  padding: 16px; margin-bottom: 16px;
`;
const FieldLbl = styled.div` font-size: 11px; color: #4B5563; margin-bottom: 6px; `;
const Pills = styled.div` display: flex; gap: 6px; flex-wrap: wrap; `;
const Pill = styled.button`
  padding: 7px 14px; border-radius: 20px; font-size: 12px; font-weight: 500;
  font-family: inherit; cursor: pointer;
  border: 1.5px solid ${({ $on }) => $on ? "#0D3318" : "#E5E7EB"};
  background: ${({ $on }) => $on ? "#0D3318" : "#fff"};
  color: ${({ $on }) => $on ? "#fff" : "#4B5563"};
`;
const Select = styled.select`
  height: 38px; border: 1.5px solid #E5E7EB; border-radius: 10px;
  padding: 0 10px; font-size: 13px; font-family: inherit; color: #111;
  background: #F9FAFB; outline: none; cursor: pointer;
  &:focus { border-color: ${GREEN}; background: #fff; }
`;
const GenerateBtn = styled.button`
  height: 40px; padding: 0 20px; border-radius: 10px; border: none;
  background: ${GREEN}; color: #fff; font-size: 13px; font-weight: 600;
  font-family: inherit; cursor: pointer;
  &:disabled { background: #E5E7EB; color: #9CA3AF; cursor: default; }
`;
const ReportHead = styled.div`
  display: flex; justify-content: space-between; align-items: flex-start; gap: 12px;
  margin-bottom: 14px;
`;
const ReportTitle = styled.div` font-size: 17px; font-weight: 700; color: #111; `;
const ReportSub = styled.div` font-size: 12px; color: #4B5563; margin-top: 2px; `;
const PdfBtn = styled.button`
  height: 36px; padding: 0 14px; border-radius: 8px;
  border: 1.5px solid #E5E7EB; background: #fff; color: #111;
  font-size: 12px; font-weight: 600; font-family: inherit; cursor: pointer;
  display: inline-flex; align-items: center; gap: 6px;
  &:disabled { opacity: .5; cursor: default; }
`;
const KpiGrid = styled.div`
  display: grid; grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
  gap: 12px; margin-bottom: 18px;
`;
const Kpi = styled.div`
  border-radius: 12px; padding: 12px 14px;
  background: ${({ $bg }) => $bg || "#F9FAFB"};
`;
const KpiLbl = styled.div` font-size: 12px; color: ${({ $c }) => $c || "#4B5563"}; `;
const KpiVal = styled.div` font-size: 22px; font-weight: 700; color: ${({ $c }) => $c || "#111"}; `;
const KpiValSm = styled.div` font-size: 15px; font-weight: 700; color: ${({ $c }) => $c || "#111"}; `;
const SectionLbl = styled.div` font-size: 12px; color: #4B5563; margin-bottom: 8px; `;
const ChartWrap = styled.div` position: relative; width: 100%; height: ${({ $h }) => $h || 220}px; margin-bottom: 20px; `;
const Table = styled.table` width: 100%; border-collapse: collapse; font-size: 13px; `;
const Th = styled.th`
  font-size: 10px; text-transform: uppercase; letter-spacing: .04em; color: #4B5563;
  padding: 8px 6px; border-bottom: 1px solid #EAECEF;
  text-align: ${({ $r }) => $r ? "right" : "left"};
`;
const Td = styled.td`
  padding: 9px 6px; border-bottom: 1px solid #F3F4F6; color: #111;
  text-align: ${({ $r }) => $r ? "right" : "left"};
  font-weight: ${({ $b }) => $b ? 600 : 400};
`;
const Badge = styled.span`
  font-size: 10px; padding: 1px 7px; border-radius: 10px; margin-left: 6px;
  background: ${({ $lo }) => $lo ? "#FDEEDA" : "#E1F5EE"};
  color: ${({ $lo }) => $lo ? "#633806" : "#085041"};
`;
const Msg = styled.div` text-align: center; color: #4B5563; padding: 40px 0; font-size: 14px; `;

/* ── helpers ── */
const kL = (litres) => literToKiloliter(parseFloat(litres || 0));
const monthLabel = (ym) => moment(ym, "YYYY-MM").format("MMM 'YY");

export default function ReportsV2() {
  const history = useHistory();
  const state = useContext(GlobalState);
  const reportRef = useRef(null);

  const [metric, setMetric] = useState("consumed"); // consumed | filled
  const [device, setDevice] = useState("oht");       // oht | sump

  /* Period: a year + a month range capped at 6 months (within that year) */
  const nowY = moment().year();
  const nowM = moment().month() + 1; // 1-12
  const years = [];
  for (let y = nowY; y >= FIRST_YEAR; y--) years.push(y);

  const [year, setYear] = useState(nowY);
  const [fromMonth, setFromMonth] = useState(Math.max(1, nowM - 5));
  const [toMonth, setToMonth] = useState(nowM);

  const maxMonth = year === nowY ? nowM : 12;             // no future months in the current year
  const fromM = `${year}-${String(fromMonth).padStart(2, "0")}`;
  const toM = `${year}-${String(toMonth).padStart(2, "0")}`;

  const changeYear = (y) => {
    const mm = y === nowY ? nowM : 12;
    setYear(y); setToMonth(mm); setFromMonth(Math.max(1, mm - 5));
  };
  const changeFrom = (fm) => {
    setFromMonth(fm);
    const hi = Math.min(fm + 5, year === nowY ? nowM : 12);  // 6-month cap
    setToMonth((t) => Math.min(Math.max(t, fm), hi));
  };
  const changeTo = (tm) => {
    setToMonth(tm);
    setFromMonth((f) => Math.min(Math.max(f, Math.max(1, tm - 5)), tm));
  };

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [exporting, setExporting] = useState(false);

  const run = useCallback(async () => {
    setLoading(true); setError(""); setData(null);
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_API}/user/reports/tank-summary` +
          `?device=${device}&metric=${metric}&from=${fromM}&to=${toM}`,
        { headers: { Authorization: state.token[0] } }
      );
      setData(res.data);
    } catch (e) {
      console.error("report fetch:", e);
      setError(
        e?.response?.status === 404
          ? "Reports API isn't available yet. Once the /user/reports/tank-summary endpoint is deployed, this will populate."
          : (e?.response?.data?.message || "Could not load the report.")
      );
    } finally { setLoading(false); }
  }, [device, metric, fromM, toM, state.token]);

  /* No auto-run — the report generates only when the user clicks Generate. */

  /* ── derive report model ── */
  const months = data?.months || [];
  const tanks = (data?.tanks || []).map(t => {
    const vals = (t.values || []).map(v => parseFloat(v || 0));
    const total = vals.reduce((a, b) => a + b, 0);
    const avg = vals.length ? total / vals.length : 0;
    return { ...t, vals, total, avg };
  });
  const sorted = [...tanks].sort((a, b) => b.avg - a.avg);
  const highest = sorted[0];
  const lowest = sorted[sorted.length - 1];
  const grandTotal = tanks.reduce((a, t) => a + t.total, 0);
  const avgPerTankMonth = tanks.length && months.length
    ? grandTotal / (tanks.length * months.length) : 0;

  const metricWord = metric === "consumed" ? "consumption" : "filled";
  const deviceWord = device === "oht" ? "OHT" : "Sump";
  const barColor = metric === "consumed" ? AMBER : GREEN;

  const exportPdf = async () => {
    if (!reportRef.current) return;
    setExporting(true);
    try {
      const html2canvas = (await import("html2canvas")).default;
      const { jsPDF } = await import("jspdf");
      const canvas = await html2canvas(reportRef.current, {
        scale: 2,
        backgroundColor: "#ffffff",
        logging: false,
        useCORS: true,
        ignoreElements: (el) => el.classList && el.classList.contains("no-print"),
      });
      const img = canvas.toDataURL("image/png");
      const pdf = new jsPDF({ orientation: "portrait", unit: "pt", format: "a4" });
      const pw = pdf.internal.pageSize.getWidth();
      const ph = pdf.internal.pageSize.getHeight();
      const imgH = (canvas.height * pw) / canvas.width;
      let y = 0;
      pdf.addImage(img, "PNG", 0, y, pw, imgH);
      let remaining = imgH - ph;
      while (remaining > 0) {                // paginate tall reports
        pdf.addPage();
        y -= ph;
        pdf.addImage(img, "PNG", 0, y, pw, imgH);
        remaining -= ph;
      }
      pdf.save(`vaaripa-${deviceWord}-${metricWord}-${fromM}_to_${toM}.pdf`);
    } catch (e) {
      console.error("pdf export:", e);
      alert("Could not generate PDF: " + e.message);
    } finally { setExporting(false); }
  };

  const cmpData = {
    labels: sorted.map(t => t.name),
    datasets: [{
      data: sorted.map(t => +(t.avg / 1000).toFixed(2)),
      backgroundColor: sorted.map(t =>
        t.sensor_id === highest?.sensor_id ? GREEN
          : t.sensor_id === lowest?.sensor_id ? AMBER : MID),
      borderRadius: 4, borderSkipped: false, barThickness: 22,
    }],
  };
  const cmpOpts = {
    indexAxis: "y", responsive: true, maintainAspectRatio: false,
    layout: { padding: { right: 48 } },
    plugins: {
      legend: { display: false },
      tooltip: { callbacks: { label: (i) => i.parsed.x + " kL / month" } },
      datalabels: {
        anchor: "end", align: "right", clamp: true,
        color: "#111827", font: { size: 11, weight: "600" },
        formatter: (v) => v,
      },
    },
    scales: {
      x: { beginAtZero: true, ticks: { callback: (v) => v + " kL", color: "#898781", font: { size: 11 } }, grid: { color: "#E1E0D9" } },
      y: { ticks: { color: "#898781", font: { size: 12 } }, grid: { display: false } },
    },
  };

  const single = tanks[0];
  const trendData = single && {
    labels: months.map(monthLabel),
    datasets: [{ data: single.vals.map(v => +(v / 1000).toFixed(2)), backgroundColor: barColor, borderRadius: 4, borderSkipped: false, barThickness: 30 }],
  };
  const trendOpts = {
    responsive: true, maintainAspectRatio: false,
    layout: { padding: { top: 20 } },
    plugins: {
      legend: { display: false },
      tooltip: { callbacks: { label: (i) => i.parsed.y + " kL" } },
      datalabels: {
        anchor: "end", align: "top", clamp: true,
        color: "#111827", font: { size: 11, weight: "600" },
        formatter: (v) => v,
      },
    },
    scales: {
      x: { grid: { display: false }, ticks: { color: "#898781", font: { size: 11 } } },
      y: { beginAtZero: true, ticks: { callback: (v) => v + " kL", color: "#898781", font: { size: 11 } }, grid: { color: "#E1E0D9" } },
    },
  };

  return (
    <Screen>
      <PullToRefresh />
      <Nav data-ptr-header>
        <BackBtn onClick={() => history.push("/")}>←</BackBtn>
        <NavTitle>Reports</NavTitle>
      </Nav>

      <Page>
        {/* BUILDER */}
        <Card>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 18, alignItems: "flex-end" }}>
            <div>
              <FieldLbl>Metric</FieldLbl>
              <Pills>
                <Pill $on={metric === "consumed"} onClick={() => setMetric("consumed")}>Consumption</Pill>
                <Pill $on={metric === "filled"} onClick={() => setMetric("filled")}>Filled</Pill>
              </Pills>
            </div>
            <div>
              <FieldLbl>Device</FieldLbl>
              <Pills>
                <Pill $on={device === "oht"} onClick={() => setDevice("oht")}>OHT</Pill>
                <Pill $on={device === "sump"} onClick={() => setDevice("sump")}>Sump</Pill>
              </Pills>
            </div>
            <div>
              <FieldLbl>Year</FieldLbl>
              <Select value={year} onChange={(e) => changeYear(Number(e.target.value))}>
                {years.map((y) => <option key={y} value={y}>{y}</option>)}
              </Select>
            </div>
            <div>
              <FieldLbl>From</FieldLbl>
              <Select value={fromMonth} onChange={(e) => changeFrom(Number(e.target.value))}>
                {Array.from({ length: maxMonth }, (_, i) => i + 1).map((m) => (
                  <option key={m} value={m}>{MONTHS[m - 1]}</option>
                ))}
              </Select>
            </div>
            <div>
              <FieldLbl>To</FieldLbl>
              <Select value={toMonth} onChange={(e) => changeTo(Number(e.target.value))}>
                {Array.from({ length: Math.min(fromMonth + 5, maxMonth) - fromMonth + 1 }, (_, i) => fromMonth + i).map((m) => (
                  <option key={m} value={m}>{MONTHS[m - 1]}</option>
                ))}
              </Select>
            </div>
            <div style={{ fontSize: 11, color: "#4B5563", alignSelf: "center" }}>Max 6 months</div>
            <GenerateBtn onClick={run} disabled={loading}>{loading ? "Generating…" : "Generate"}</GenerateBtn>
          </div>
        </Card>

        {!loading && !error && !data && (
          <Msg>Choose a metric, device and period, then click Generate.</Msg>
        )}
        {loading && <Msg>Generating report…</Msg>}
        {!loading && error && <Msg>{error}</Msg>}
        {!loading && !error && data && tanks.length === 0 && (
          <Msg>No {deviceWord} {metricWord} data for this period.</Msg>
        )}

        {!loading && !error && tanks.length > 0 && (
          <div ref={reportRef} style={{ background: "#fff", borderRadius: 16, border: "1px solid #EAECEF", padding: 18 }}>
            <div style={{ marginBottom: 14, paddingBottom: 12, borderBottom: "1px solid #EAECEF" }}>
              <div style={{ width: 130 }}><Logo /></div>
            </div>
            <ReportHead>
              <div>
                <ReportTitle>{deviceWord} {metricWord} report</ReportTitle>
                <ReportSub>
                  {(data.org || "")} · {tanks.length} {tanks.length === 1 ? deviceWord.toLowerCase() : deviceWord.toLowerCase() + "s"} · {monthLabel(fromM)} – {monthLabel(toM)}
                </ReportSub>
              </div>
              <PdfBtn onClick={exportPdf} disabled={exporting} className="no-print">
                {exporting ? "Exporting…" : "⬇ PDF"}
              </PdfBtn>
            </ReportHead>

            {/* ── Multi-tank comparison ── */}
            {tanks.length > 1 ? (
              <>
                <KpiGrid>
                  <Kpi><KpiLbl>Total {metricWord}</KpiLbl><KpiVal>{kL(grandTotal)}</KpiVal></Kpi>
                  <Kpi><KpiLbl>Avg / tank / month</KpiLbl><KpiVal>{kL(avgPerTankMonth)}</KpiVal></Kpi>
                  <Kpi $bg="#E1F5EE"><KpiLbl $c="#0F6E56">Highest avg</KpiLbl><KpiValSm $c="#085041">{highest?.name}</KpiValSm><KpiLbl $c="#0F6E56">{kL(highest?.avg)} / mo</KpiLbl></Kpi>
                  <Kpi $bg="#FDEEDA"><KpiLbl $c="#854F0B">Lowest avg</KpiLbl><KpiValSm $c="#633806">{lowest?.name}</KpiValSm><KpiLbl $c="#854F0B">{kL(lowest?.avg)} / mo</KpiLbl></Kpi>
                </KpiGrid>

                <SectionLbl>Average {metricWord} per tank (kL / month)</SectionLbl>
                <ChartWrap $h={Math.max(160, sorted.length * 46)}>
                  <Bar data={cmpData} options={cmpOpts} />
                </ChartWrap>

                <SectionLbl>Monthly breakdown (kL)</SectionLbl>
                <Table>
                  <thead>
                    <tr>
                      <Th>Tank</Th>
                      {months.map(m => <Th $r key={m}>{monthLabel(m)}</Th>)}
                      <Th $r>Avg</Th>
                    </tr>
                  </thead>
                  <tbody>
                    {sorted.map(t => (
                      <tr key={t.sensor_id}>
                        <Td>
                          {t.name}
                          {t.sensor_id === highest?.sensor_id && <Badge>Highest</Badge>}
                          {t.sensor_id === lowest?.sensor_id && <Badge $lo>Lowest</Badge>}
                        </Td>
                        {t.vals.map((v, i) => <Td $r key={i}>{(v / 1000).toFixed(2)}</Td>)}
                        <Td $r $b>{(t.avg / 1000).toFixed(2)}</Td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </>
            ) : (
              /* ── Single-tank across months ── */
              <>
                <KpiGrid>
                  <Kpi><KpiLbl>Total {metricWord}</KpiLbl><KpiVal>{kL(single.total)}</KpiVal></Kpi>
                  <Kpi><KpiLbl>Monthly average</KpiLbl><KpiVal>{kL(single.avg)}</KpiVal></Kpi>
                  <Kpi><KpiLbl>Months</KpiLbl><KpiVal>{months.length}</KpiVal></Kpi>
                </KpiGrid>
                <SectionLbl>{single.name} — {metricWord} across months</SectionLbl>
                <ChartWrap $h={200}><Bar data={trendData} options={trendOpts} /></ChartWrap>
                <Table>
                  <thead>
                    <tr><Th>Month</Th><Th $r>{metricWord === "consumption" ? "Consumed" : "Filled"} (kL)</Th></tr>
                  </thead>
                  <tbody>
                    {months.map((m, i) => (
                      <tr key={m}><Td>{monthLabel(m)}</Td><Td $r>{(single.vals[i] / 1000).toFixed(2)}</Td></tr>
                    ))}
                  </tbody>
                </Table>
              </>
            )}
          </div>
        )}
      </Page>
    </Screen>
  );
}
